
import { useEffect, useState, useRef } from "react";
import { Box, Stack } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2"; // Grid version 2
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import ImageIcon from "@mui/icons-material/Image";
import { useTranslate } from "src/locales/use-locales";
import { useAppSelector } from "src/app/hooks";
import { useCardForm } from "src/hooks/useCardForm";
import { referenceService } from "src/api/references/referenceService";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { calculateAge } from "src/utils/date/dateHelper";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ImagePanel from "../image-panel";

interface BradenFormData {
  PSRB_ID: string;
  PROCESS_TIME: Date | null;
  CUSER: string;
  CTIME: Date | null;
  NURSE_ID: string;
  SENSORY_ASSESSMENT_ID: string;
  SKIN_MOISTURE_CONDITION_ID: string;
  MOVEMENT_ABILITY_ID: string;
  PHYSICAL_ACTIVITY_ID: string;
  NUTRITION_ID: string;
  FRICTION_PRESSURE_ID: string;
  PERFUSION_OXYGENATION_ID: string;
  RISK_ASSESSMENT_ID: string;
  CALC_VALUE: number;
  PRESSURE_WOUND_RATE_ID: string;
  PRESS_SCORE_AREA_ID: string;
  IS_BRADEN: string; // Bası Yarası (Var/Yok)
  NOTE: string;
  // Extra fields mapping
  GESTATION_TIME_ID: string;
  NRB_ID: string;
  MNYPSD_ID: string;
  MPSRBD_ID: string;
}

interface BradenProps {
  cardType?: string; // Generic approach to support subclasses (BBQ, Q)
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  save: (id: string) => Promise<any>;
  handleChange: (field: keyof BradenFormData, value: any) => void;
  patientData: any;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function BasiBraden({
  cardType,
  recordId,
  patientId,
  onClose,
  save,
  handleChange: parentHandleChange,
  patientData,
  isSaving,
  extraAccess,
}: BradenProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("error");
  const [imagePanelOpen, setImagePanelOpen] = useState(false);

  const {
    formData,
    selectedMCPs,
    handleChange,
    handleMCPChange,
    cardData,
    getMCPParameterDetails,
    getScoreValue,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: cardType || "BASI_BRADEN_RISK_SCORE",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof BradenFormData, value);
      });
    },
  });

  const typedFormData = formData as unknown as BradenFormData;

  // Determine Braden Type based on Age - Visual / UI adjustments
  const [bradenType, setBradenType] = useState<"B" | "BQ" | "BBQ">("B");

  useEffect(() => {
    // If cardType is explicit, use it to set bradenType
    if (cardType === "BASI_BRADEN_RISK_SCORE_BBQ") {
      setBradenType("BBQ");
      return;
    } else if (cardType === "BASI_BRADEN_RISK_SCORE_Q") {
      setBradenType("BQ");
      return;
    } else if (cardType === "BASI_BRADEN_RISK_SCORE") {
      setBradenType("B");
      return;
    }

    if (patientData?.data?.birthdate) {
      const age = calculateAge(patientData.data.birthdate);
      if (age) {
        if (age.totalDays <= 28) {
          setBradenType("BBQ");
        } else if (age.years < 6) {
          setBradenType("BQ");
        } else {
          setBradenType("B");
        }
      }
    }
  }, [patientData, cardType]);

  // Sync to parent continuously
  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      let val = value;
      // Truncate strings if needed
      if (typeof val === "string" && val.length > 200) {
        val = val.substring(0, 200);
      }
      parentHandleChange(key as keyof BradenFormData, val);
    });
  }, [formData, parentHandleChange]);

  // Score Calculation
  const [score, setScore] = useState(0);

  useEffect(() => {
    if (!cardData) return;
    let total = 0;

    // List of keys influencing score
    const fieldsToSum = [
      "SENSORY_ASSESSMENT_ID", "SKIN_MOISTURE_CONDITION_ID", "MOVEMENT_ABILITY_ID",
      "PHYSICAL_ACTIVITY_ID", "NUTRITION_ID", "FRICTION_PRESSURE_ID",
      "PERFUSION_OXYGENATION_ID", "GESTATION_TIME_ID",
      // Mapped keys
      "MOBILITY_NEWBORN_ID", "ACTIVITY_NEWBORN_ID", "SENSORY_PERCEPTION_ID",
      "MOISTURE_CONDITION_ID", "FRICTION_PEELING_ID", "NUTRITION_NEWBORN_ID",
    ];

    fieldsToSum.forEach(field => {
      const val = getScoreValue(field);
      if (val && !isNaN(val)) total += val;
    });

    const finalTotal = isNaN(total) ? 0 : total;

    setScore((prevScore) => {
      if (prevScore !== finalTotal) {
        handleChange("CALC_VALUE", finalTotal);
        return finalTotal;
      }
      return prevScore;
    });
  }, [selectedMCPs, cardData, getScoreValue, handleChange]);

  const [localIsSaving, setLocalIsSaving] = useState(false);

  const handleSave = async (id?: string) => {
    try {
      setLocalIsSaving(true);
      await save(id || "");
    } catch (error) {
      console.error("Save Error:", error);
      setAlertMessage(t("card.save-error"));
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLocalIsSaving(false);
    }
  };

  const renderMCPField = (
    fieldName: string,
    labelString: string,
    mcpId?: number, // Optional override if we know it
  ) => {
    // If mcpId is not passed, try to get from config. 
    // If config has 0 (placeholder), we might have issues fetching options.
    // For now, relies on config.
    const configId = cardData?.mcpConfig?.[fieldName as keyof typeof cardData.mcpConfig]?.mcpId || mcpId || 0;

    return (
      <Grid container alignItems="stretch" justifyContent="space-between">
        <Grid
          xs={12}
          sm={6}
          md={6}
          sx={{
            borderBottom: "1px solid #E0E0E0",
            display: "flex",
            alignItems: "center",
            minHeight: "56px"
          }}
        >
          <ByzMuiTypography sx={{ marginLeft: "30px" }}>
            {labelString}
          </ByzMuiTypography>
        </Grid>
        <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center" }}>
          <ByzMcParameterDetailSelect
            title={labelString}
            mcpId={configId}
            multiple={false}
            handleChange={(val) => {
              if (!Array.isArray(val)) {
                handleMCPChange(fieldName, val);
              }
            }}
            value={selectedMCPs[fieldName]}
            sx={{ width: "100%" }}
            cachedData={getMCPParameterDetails(configId)}
            cacheLoaded={mcpCacheLoaded}
            readOnly={!extraAccess}
          />
        </Grid>
      </Grid>
    );
  };

  const renderSectionHeader = (title: string) => (
    <Grid
      md={12}
      sx={{
        backgroundColor: "#F4F7FE",
        borderBottom: "1px solid #E0E0E0",
      }}
    >
      <ByzMuiTypography isTitle={true}>
        {title}
      </ByzMuiTypography>
    </Grid>
  );

  const { selectedOfficeId } = useAppSelector((state) => state.auth);

  const getRecordIdForFile = () => {
    // Priority: Detail ID (recordId) > Master IDs
    return recordId || typedFormData.PSRB_ID || (typedFormData as any).NRB_ID;
  };

  const getSubIdForFile = () => {
    if (bradenType === "BBQ") {
      return (typedFormData as any).MNYPSD_ID || (typedFormData as any).ID || (typedFormData as any).id || recordId;
    }
    return (typedFormData as any).MPSRBD_ID || (typedFormData as any).ID || (typedFormData as any).id || recordId;
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const id = getRecordIdForFile();
    if (!id) {
      setAlertMessage(t("card.please-save-record-first-for-visuals"));
      setAlertType("error");
      setAlertOpen(true);
      return;
    }

    try {
      setLocalIsSaving(true);
      await referenceService.mcBradenFile.upload({
        prsdId: parseInt(id),
        ptId: patientId,
        offId: selectedOfficeId || 0,
        mnypsdId: bradenType === "BBQ" ? parseInt(getSubIdForFile()) : undefined,
        mpsrbdId: (bradenType === "B" || bradenType === "BQ") ? parseInt(getSubIdForFile()) : undefined,
        file
      });
      setAlertMessage(t("common.success"));
      setAlertType("success");
      setAlertOpen(true);
    } catch (error: any) {
      console.error("Error uploading file:", error);
      const errorMessage = error?.response?.data?.message || error?.message || t("common.error");
      setAlertMessage(errorMessage);
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLocalIsSaving(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const triggerUpload = () => {
    const id = getRecordIdForFile();
    if (!id) {
      setAlertMessage(t("card.please-save-record-first-for-visuals"));
      setAlertType("error");
      setAlertOpen(true);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleOpenVisuals = () => {
    const id = getRecordIdForFile();
    if (!id) {
      setAlertMessage(t("card.please-save-record-first-for-visuals"));
      setAlertType("error");
      setAlertOpen(true);
      return;
    }
    setImagePanelOpen(true);
  };

  return (
    <Box sx={{ padding: "20px", width: "100%" }}>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileUpload}
        accept=".jpg,.jpeg,.png,.gif,.bmp"
      />
      <Box mb={3} display="flex" justifyContent="space-between">
        <Stack direction="row" spacing={2}>
          <OtherPrivilegeGuard extraAccess={!!extraAccess} fallback={<ByzMuiSaveButton disabled />}>
            <ByzMuiSaveButton onClick={() => handleSave(getRecordIdForFile())} loading={isSaving || localIsSaving} />
          </OtherPrivilegeGuard>

          <ByzMuiButton
            onClick={triggerUpload}
            icon={<ImageIcon />}
            loading={localIsSaving}
          >
            {t("common.add-visual")}
          </ByzMuiButton>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        <Grid xs={12} sm={6} md={6} container direction={"column"} spacing={3}>
          {/* Header / Info */}

          <Grid
            md={12}
            sx={{ backgroundColor: "#F4F7FE", borderBottom: "1px solid #E0E0E0" }}
          >
            <ByzMuiTypography isTitle={true}>
              {bradenType === "B" && t("basi-braden.pressure-sore-risk-braden")}
              {bradenType === "BQ" && t("basi-braden.modified-braden-q-scale")}
              {bradenType === "BBQ" && t("basi-braden.newborn-braden-q-scale")}
            </ByzMuiTypography>
          </Grid>

          <Grid container alignItems="stretch" justifyContent="space-between">
            <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}>
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("datetime")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center" }}>
              <ByzMuiDateTimePicker
                sx={{ width: "100%" }}
                value={typedFormData.PROCESS_TIME}
                onChange={(date) => handleChange("PROCESS_TIME", date)}
                disabled={!extraAccess}
              />
            </Grid>
          </Grid>

          <Grid container alignItems="stretch" justifyContent="space-between">
            <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}>
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                {t("createdby")}
              </ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center" }}>
              <ByzMuiTextField
                sx={{ width: "100%" }}
                value={typedFormData.CUSER}
                disabled
              />
            </Grid>
          </Grid>

          {/* BBQ Specific Fields */}
          {bradenType === "BBQ" && (
            <>
              {renderSectionHeader(t("basi-braden.general-physical-conditions"))}
              {renderMCPField("IS_BRADEN", t("basi-braden.pressure-sore"))}
              {renderMCPField("PRESS_SCORE_AREA_ID", t("basi-braden.pressure-sore-area"))}
              {renderMCPField("PRESSURE_WOUND_RATE_ID", t("basi-braden.pressure-sore-rate"))}
              {renderMCPField("RISK_ASSESSMENT_ID", t("basi-braden.risk-assessment"))}
              {renderMCPField("GESTATION_TIME_ID", t("basi-braden.pregnancy-duration"))}
              {renderMCPField("MOBILITY_NEWBORN_ID", t("basi-braden.mobility"))}
              {renderMCPField("ACTIVITY_NEWBORN_ID", t("basi-braden.activity"))}
              {renderMCPField("SENSORY_PERCEPTION_ID", t("basi-braden.sensory-perception"))}

              {renderSectionHeader(t("basi-braden.tolerance-of-skin-and-attachments"))}
              {renderMCPField("NUTRITION_NEWBORN_ID", t("basi-braden.nutrition"))}
              {renderMCPField("MOISTURE_CONDITION_ID", t("basi-braden.moisture"))}
              {renderMCPField("FRICTION_PEELING_ID", t("basi-braden.friction-peeling"))}

              {/* Visual Row */}
              <Grid container alignItems="stretch" justifyContent="space-between">
                <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}>
                  <ByzMuiTypography sx={{ marginLeft: "30px" }}>{t("basi-braden.visual")}</ByzMuiTypography>
                </Grid>
                <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center" }}>
                  <ByzMuiButton
                    variant2="outlined"
                    onClick={handleOpenVisuals}
                    icon={<ImageIcon />}
                    sx={{ width: "100%", height: "100%", borderRadius: 0 }}
                  >
                    {t("common.open")}
                  </ByzMuiButton>
                </Grid>
              </Grid>

              {renderMCPField("PERFUSION_OXYGENATION_ID", t("basi-braden.tissue-perfusion-and-oxygenation"))}
            </>
          )}

          {/* B or BQ Specific Fields */}
          {(bradenType === "B" || bradenType === "BQ") && (
            <>
              {renderSectionHeader(t("basi-braden.pressure-intensity-and-duration"))}
              {renderMCPField("IS_BRADEN", t("basi-braden.pressure-sore"))}
              {renderMCPField("PRESS_SCORE_AREA_ID", t("basi-braden.pressure-sore-area"))}
              {renderMCPField("PRESSURE_WOUND_RATE_ID", t("basi-braden.pressure-sore-rate"))}
              {renderMCPField("RISK_ASSESSMENT_ID", t("basi-braden.risk-assessment"))}
              {renderMCPField("SENSORY_ASSESSMENT_ID", t("basi-braden.sensory-assessment"))}
              {renderMCPField("PHYSICAL_ACTIVITY_ID", t("basi-braden.physical-activity"))}
              {renderMCPField("MOVEMENT_ABILITY_ID", t("basi-braden.movement-ability"))}

              {renderSectionHeader(t("basi-braden.supporting-surface-and-tolerance"))}
              {renderMCPField("NUTRITION_ID", t("basi-braden.nutrition"))}
              {renderMCPField("SKIN_MOISTURE_CONDITION_ID", t("basi-braden.skin-moisture-status"))}
              {renderMCPField("FRICTION_PRESSURE_ID", t("basi-braden.friction-pressure-irritation"))}

              {/* Visual Row */}
              <Grid container alignItems="stretch" justifyContent="space-between">
                <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}>
                  <ByzMuiTypography sx={{ marginLeft: "30px" }}>{t("basi-braden.visual")}</ByzMuiTypography>
                </Grid>
                <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center" }}>
                  <ByzMuiButton
                    variant2="outlined"
                    onClick={handleOpenVisuals}
                    icon={<ImageIcon />}
                    sx={{ width: "100%", height: "100%", borderRadius: 0 }}
                  >
                    {t("common.open")}
                  </ByzMuiButton>
                </Grid>
              </Grid>

              {bradenType === "BQ" && renderMCPField("PERFUSION_OXYGENATION_ID", t("basi-braden.tissue-perfusion-and-oxygenation"))}
            </>
          )}

          {/* Score & Note */}
          {renderSectionHeader(t("common.other"))}
          <Grid container alignItems="stretch" justifyContent="space-between">
            <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}>
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>{t("score")}</ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center" }}>
              <ByzMuiTextField value={score} disabled sx={{ width: "100%" }} />
            </Grid>
          </Grid>
          <Grid container alignItems="stretch" justifyContent="space-between">
            <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}>
              <ByzMuiTypography sx={{ marginLeft: "30px" }}>{t("note")}</ByzMuiTypography>
            </Grid>
            <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center" }}>
              <ByzMuiTextField
                multiline
                value={typedFormData.NOTE}
                onChange={(e) => handleChange("NOTE", e.target.value)}
                disabled={!extraAccess}
                sx={{ width: "100%" }}
              />
            </Grid>
          </Grid>

        </Grid>
      </Grid>

      <OpenComponentDialog
        open={imagePanelOpen}
        onClose={() => setImagePanelOpen(false)}
        title={t("buttons.visuals")}
        maxWidth="lg"
        showPatientData={false}
      >
        <ImagePanel
          prsdId={parseInt(getSubIdForFile())}
          ptId={patientId}
          offId={selectedOfficeId || 0}
          mnypsdId={bradenType === "BBQ" ? parseInt(getSubIdForFile()) : undefined}
          mpsrbdId={(bradenType === "B" || bradenType === "BQ") ? parseInt(getSubIdForFile()) : undefined}
          extraAccess={extraAccess}
          onUpload={async (file) => {
            await referenceService.mcBradenFile.upload({
              prsdId: parseInt(getSubIdForFile()),
              ptId: patientId,
              offId: selectedOfficeId || 0,
              mnypsdId: bradenType === "BBQ" ? parseInt(getSubIdForFile()) : undefined,
              mpsrbdId: (bradenType === "B" || bradenType === "BQ") ? parseInt(getSubIdForFile()) : undefined,
              file
            });
          }}
          onUpdate={async (id, file) => {
            await referenceService.mcBradenFile.update(id, {
              ptId: patientId,
              offId: selectedOfficeId || 0,
              file
            });
          }}
        />
      </OpenComponentDialog>

      <ByzMuiSnackBar
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        title={alertType === "success" ? t("success") : t("warning")}
        message={alertMessage}
        type={alertType}
      />
    </Box>
  );
}

export default BasiBraden;
